import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { generatePaymentLink } from '../../services/userService';

interface InsufficientCreditsModalProps {
    isOpen: boolean;
    onClose: () => void;
    requiredCredits: number;
    availableCredits: number;
    featureName: string;
}

const InsufficientCreditsModal: React.FC<InsufficientCreditsModalProps> = ({
    isOpen,
    onClose,
    requiredCredits,
    availableCredits,
    featureName
}) => {
    const { currentUser } = useAuth();
    const [paymentLoading, setPaymentLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleUpgrade = async () => {
        if (!currentUser?.uid) return;

        try {
            setPaymentLoading(true);
            setError(null);
            const { paymentLink } = await generatePaymentLink('Pro', currentUser.uid, 0);
            window.open(paymentLink, '_blank');
        } catch (error) {
            console.error('Failed to generate payment link:', error);
            setError('Failed to generate payment link. Please try again.');
        } finally {
            setPaymentLoading(false);
        }
    };

    if (!isOpen) return null;

    const creditsNeeded = requiredCredits - availableCredits;

    return (
        <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={onClose}
        >
            <div
                className="bg-white rounded-lg w-full max-w-sm sm:max-w-md md:max-w-lg lg:max-w-2xl xl:max-w-4xl max-h-[90vh] overflow-y-auto relative"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Close button */}
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 z-10 bg-accent-sand hover:bg-accent-taupe text-text-description rounded-full p-2 transition-colors"
                    title="Close"
                >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>

                <div className="max-w-4xl mx-auto">
                    {/* Header with feature info */}
                    <div className="bg-gradient-to-r from-red-600 to-orange-600 text-white p-8 text-center rounded-t-lg">
                        <div className="w-20 h-20 mx-auto mb-4 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                            <i className="fas fa-coins text-3xl"></i>
                        </div>
                        <h1 className="text-3xl font-bold mb-2">Insufficient Credits</h1>
                        <p className="text-red-100 text-lg">You need more credits to use {featureName}</p>
                    </div>

                    {/* Content */}
                    <div className="p-8">
                        <div className="text-center mb-8">
                            <h2 className="text-2xl font-semibold text-text-heading mb-4">
                                Not Enough Credits for This Generation
                            </h2>

                            {/* Credit Info */}
                            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 max-w-md mx-auto">
                                <div className="flex items-center justify-between mb-2">
                                    <span className="text-text-heading">Required Credits:</span>
                                    <span className="font-semibold text-red-600">{requiredCredits.toFixed(2)} credits</span>
                                </div>
                                <div className="flex items-center justify-between mb-2">
                                    <span className="text-text-heading">Your Credits:</span>
                                    <span className="font-semibold text-text-description">{availableCredits.toFixed(2)} credits</span>
                                </div>
                                <div className="border-t border-red-200 pt-2 mt-2">
                                    <div className="flex items-center justify-between">
                                        <span className="text-text-heading font-medium">Credits Needed:</span>
                                        <span className="font-bold text-red-600">{creditsNeeded.toFixed(2)} credits</span>
                                    </div>
                                </div>
                            </div>

                            <p className="text-text-description">
                                Upgrade to Pro Plan to get monthly credits and unlock all premium features!
                            </p>
                        </div>

                        {/* Error Message */}
                        {error && (
                            <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-6 text-center">
                                <p className="text-sm text-red-600">{error}</p>
                            </div>
                        )}

                        {/* Pro Plan Benefits */}
                        <div className="bg-accent-sand rounded-lg p-6 mb-8">
                            <h3 className="text-lg font-semibold text-text-heading mb-4 text-center">
                                Pro Plan Benefits
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="flex items-center space-x-3">
                                    <i className="fas fa-check-circle text-green-500"></i>
                                    <span className="text-text-heading">40 Credits Every Month</span>
                                </div>
                                <div className="flex items-center space-x-3">
                                    <i className="fas fa-check-circle text-green-500"></i>
                                    <span className="text-text-heading">All Premium Features Unlocked</span>
                                </div>
                                <div className="flex items-center space-x-3">
                                    <i className="fas fa-check-circle text-green-500"></i>
                                    <span className="text-text-heading">Canvas Editor & Video Generation</span>
                                </div>
                                <div className="flex items-center space-x-3">
                                    <i className="fas fa-check-circle text-green-500"></i>
                                    <span className="text-text-heading">Unlimited Gallery Storage</span>
                                </div>
                                <div className="flex items-center space-x-3">
                                    <i className="fas fa-check-circle text-green-500"></i>
                                    <span className="text-text-heading">Priority Support</span>
                                </div>
                                <div className="flex items-center space-x-3">
                                    <i className="fas fa-check-circle text-green-500"></i>
                                    <span className="text-text-heading">Advanced AI Models</span>
                                </div>
                            </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <button
                                onClick={handleUpgrade}
                                disabled={paymentLoading}
                                className="bg-secondary-500 hover:bg-secondary-600 text-white px-8 py-3 rounded-lg font-medium transition-colors disabled:opacity-50 flex items-center justify-center space-x-2"
                            >
                                {paymentLoading ? (
                                    <>
                                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                                        <span>Processing...</span>
                                    </>
                                ) : (
                                    <>
                                        <i className="fas fa-crown"></i>
                                        <span>Upgrade to Pro Plan</span>
                                    </>
                                )}
                            </button>

                            <Link
                                to="/subscription"
                                className="bg-accent-sand hover:bg-accent-taupe text-text-heading px-8 py-3 rounded-lg font-medium transition-colors text-center flex items-center justify-center space-x-2"
                            >
                                <i className="fas fa-tag"></i>
                                <span>Buy Credits</span>
                            </Link>
                        </div>

                        {/* Additional Info */}
                        <div className="mt-8 pt-6 border-t border-accent-taupe text-center">
                            <p className="text-sm text-text-description">
                                Questions about credits or upgrading?{' '}
                                <a
                                    href="mailto:support@snapmydesign.com?subject=Credits and Pro Plan Question"
                                    className="text-secondary-600 hover:text-secondary-700 font-medium"
                                >
                                    Contact our support team
                                </a>
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default InsufficientCreditsModal; 